import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, RotateCw, CalendarClock, ChevronLeft, ChevronRight } from 'lucide-react';
import { journalAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const hFont = { fontFamily: 'Manrope, sans-serif' };

export default function JournalPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const PAGE_SIZE = 50;

  const fetchEntries = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const res = await journalAPI.getAll({ page: p, page_size: PAGE_SIZE });
      setEntries(res.data.items || []);
      setTotalPages(res.data.pages || 1);
      setTotalItems(res.data.total || 0);
    } catch {
      setEntries([]);
    }
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchEntries(page); }, [fetchEntries, page]);

  const filtered = entries.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      e.description?.toLowerCase().includes(q) ||
      e.reference?.toLowerCase().includes(q) ||
      String(e.entry_number).includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="journal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-stone-900" style={hFont}>Journal Entries</h2>
          <p className="text-sm text-stone-500">{totalItems} entries</p>
        </div>
        <Button
          onClick={() => navigate('/journal/new')}
          className="bg-[#D94E18] hover:bg-[#C2410C] text-white h-9 px-5 text-sm font-semibold rounded-full"
          data-testid="new-journal-entry-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New Entry
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search entries..." className="pl-9 h-9 text-sm"
          data-testid="journal-search"
        />
      </div>

      {/* Entries list */}
      {loading ? (
        <div className="text-center py-12 text-sm text-stone-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-stone-500">No journal entries found.</p>
          <Button
            onClick={() => navigate('/journal/new')}
            variant="outline" className="mt-3 h-9 text-sm rounded-full"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create your first entry
          </Button>
        </div>
      ) : (
        <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
          <Accordion type="single" collapsible>
            {filtered.map((entry) => (
              <AccordionItem key={entry.id} value={entry.id} className="border-b border-stone-100 last:border-0">
                <AccordionTrigger className="px-4 py-3 hover:bg-stone-50 hover:no-underline" data-testid={`je-row-${entry.entry_number}`}>
                  <div className="flex items-center gap-4 text-left w-full pr-4">
                    <span className="text-xs font-mono text-stone-400 w-12 shrink-0">#{entry.entry_number}</span>
                    <span className="text-xs text-stone-500 w-20 shrink-0">{formatDate(entry.date)}</span>
                    <span className="text-sm font-medium text-stone-900 flex-1 truncate">{entry.description}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {entry.recurring_group_id && (
                        <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700 bg-blue-50 gap-1">
                          <RotateCw className="w-2.5 h-2.5" /> Recurring
                        </Badge>
                      )}
                      {entry.is_accrual && (
                        <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700 bg-amber-50 gap-1">
                          <CalendarClock className="w-2.5 h-2.5" /> Accrual
                        </Badge>
                      )}
                      {entry.accrual_reversal_of && (
                        <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-700 bg-purple-50 gap-1">
                          Reversal
                        </Badge>
                      )}
                    </div>
                    {entry.reference && (
                      <span className="text-xs text-stone-400 shrink-0">{entry.reference}</span>
                    )}
                    <span className="text-sm font-bold text-stone-900 w-24 text-right shrink-0">
                      {formatCurrency(entry.lines?.reduce((s, l) => s + (l.debit || 0), 0))}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="px-4 pb-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-stone-500 border-b border-stone-100">
                          <th className="text-left py-1.5 font-medium">Account</th>
                          <th className="text-right py-1.5 font-medium w-24 sm:w-28">Debit</th>
                          <th className="text-right py-1.5 font-medium w-24 sm:w-28">Credit</th>
                          <th className="text-left py-1.5 font-medium pl-4 hidden sm:table-cell">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.lines?.map((line) => (
                          <tr key={`${line.account_code}-${line.debit}-${line.credit}`} className="border-b border-stone-50 last:border-0">
                            <td className="py-1.5 text-stone-800 text-xs sm:text-sm">
                              <span className="text-stone-400 font-mono text-[10px] sm:text-xs mr-1">{line.account_code}</span>
                              <span className="hidden sm:inline">{line.account_name}</span>
                              <span className="sm:hidden">{(line.account_name || '').slice(0, 15)}{(line.account_name || '').length > 15 ? '...' : ''}</span>
                            </td>
                            <td className="py-1.5 text-right font-mono text-stone-900 text-xs sm:text-sm">
                              {line.debit > 0 ? formatCurrency(line.debit) : ''}
                            </td>
                            <td className="py-1.5 text-right font-mono text-stone-900 text-xs sm:text-sm">
                              {line.credit > 0 ? formatCurrency(line.credit) : ''}
                            </td>
                            <td className="py-1.5 text-stone-500 pl-4 text-xs hidden sm:table-cell">{line.description || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2" data-testid="journal-pagination">
          <p className="text-xs text-stone-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-8">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
